/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useRef, useState } from 'react';

import envConfig from '../../config/envConfig';
import { useTheme } from '../../theme/ThemeContext';
import { afterMinimumLoader, asTextUpload, isHtmlFile, publicFileLink } from './certificateTemplates';

/**
 * The read-only Template URL field with its upload button at the right edge.
 * Uploads a template's .html file to file storage as a PUBLIC file, the way
 * the File Manager does, and hands the link it gets back to `onUploaded`,
 * which fills the field.
 */
export default function CertificateFileUpload({ organizationId, applicationId, value, onUploaded, onUploadingChange, error: fieldError }) {
  const { theme } = useTheme();
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    if (!isHtmlFile(file)) { setError('Choose the template\'s .html file.'); return; }

    setUploading(true);
    onUploadingChange?.(true);
    const startedAt = Date.now();
    try {
      const body = new FormData();
      body.append('file', asTextUpload(file));
      body.append('file_type', 'text');
      body.append('public', 'true');
      const headers = { 'X-API-KEY': envConfig.fileServerApiKey, 'X-ORG-ID': organizationId };
      if (applicationId) headers['X-APP-ID'] = applicationId;
      const resp = await fetch(`${String(envConfig.fileServerUrl).replace(/\/$/, '')}/upload`, {
        method: 'POST', headers, body,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `Upload failed (${resp.status}).`);
      const url = publicFileLink(envConfig.fileServerUrl, data.public_url);
      if (!url) throw new Error('The upload gave no public link.');
      await afterMinimumLoader(startedAt);
      setFileName(file.name);
      onUploaded(url);
    } catch (err) {
      await afterMinimumLoader(startedAt);
      setError(err.message || 'Upload failed.');
    }
    setUploading(false);
    onUploadingChange?.(false);
  };

  const shownError = error || fieldError;
  return (
    <div>
      <input ref={input} type="file" accept=".html,.htm" onChange={upload} style={{ display: 'none' }} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: 4,
        border: `1.4px solid ${shownError ? '#d53b3b' : theme.primary}`,
        borderRadius: theme.buttonRadius, background: '#f3f4f6',
      }}>
        <input
          readOnly
          value={value || ''}
          placeholder="Upload the HTML file →"
          aria-label="Template URL"
          title={value || ''}
          style={{
            flex: 1, minWidth: 0, border: 'none', background: 'none', outline: 'none',
            padding: '5px 7px', fontFamily: 'monospace', fontSize: 13, color: '#374151',
          }}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => input.current?.click()}
          title={value ? 'Replace the template HTML file' : 'Upload the template HTML file'}
          style={{
            flex: 'none', border: `1.5px solid ${theme.primary}`, borderRadius: theme.buttonRadius,
            background: '#fff', color: theme.primary, padding: '5px 12px', fontSize: 14,
            cursor: uploading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {uploading
            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 6 }} />
            : <i className="bi bi-cloud-upload" style={{ marginRight: 6 }} />}
          {uploading ? 'Uploading…' : (value ? 'Replace HTML file' : 'Upload template HTML file')}
        </button>
      </div>
      {uploading && (
        <div role="status" style={{ marginTop: 6, fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="spinner-border spinner-border-sm" aria-hidden="true" style={{ color: theme.primary }} />
          Uploading the template to file storage… keep this page open.
        </div>
      )}
      {fileName && !error && !uploading && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#166534' }}>
          <i className="bi bi-check2-circle" /> Uploaded {fileName}. Its link is filled in.
        </div>
      )}
      {shownError && <div style={{ marginTop: 4, color: '#d53b3b', fontSize: 12 }}>{shownError}</div>}
    </div>
  );
}
